import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, Heart, X, Gamepad2, Pencil, Users, ChevronRight, Trash2,
  Plus, Sparkles, Trophy, MessageSquare, ChevronLeft, Check, Star,
  UserPlus, ShieldCheck, ClipboardCheck,
} from "lucide-react";

/**
 * Co-op Matchmaking — V2 (iter 51).
 *
 * Three views:
 *   - onboarding (no profile yet): emoji + 5 free-text game inputs + platform picker → POST /coop/profile
 *   - deck (has profile + opt-in): swipeable stack of up to 5 best-match cards
 *   - matches drawer: list of mutual likes
 *
 * Card swipe UX:
 *   - drag horizontally, threshold ±120 px commits a like/dislike
 *   - tap the card to expand a detail modal with breakdown pills, shared games, action buttons
 */

const PLATFORMS = ["PS4", "PS5", "Steam", "Nintendo", "Xbox", "Mobile"];

export default function CoopV2() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);     // /coop/me response
  const [tab, setTab] = useState("deck"); // deck | matches | profile

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/coop/me");
      setMe(data);
    } catch { setMe({ error: true }); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!me) return <CenterLoader />;
  if (me.error) return <div className="text-center text-sm text-[#71717A] py-20">Co-op matchmaking unavailable.</div>;

  return (
    <div className="space-y-5 pb-8" data-testid="v2-coop">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#A78BFA] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> // Co-op Matchmaking
          </div>
          <h1 className="text-2xl font-extrabold text-white mt-0.5">Find your squad</h1>
        </div>
        <Link to="/v2/explore" className="text-[11px] font-mono text-[#71717A] hover:text-white inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> back
        </Link>
      </header>

      {!me.opted_in || !me.profile ? (
        <Onboarding refresh={refresh} initial={me.profile} />
      ) : (
        <>
          <nav className="flex gap-2 -mx-1 px-1 overflow-x-auto" role="tablist">
            {[
              ["deck",      "Deck",      Sparkles],
              ["matches",   "Matches",   Trophy],
              ["groups",    "Groups",    Users],
              ["completed", "Completed", ClipboardCheck],
              ["profile",   "Profile",   Pencil],
            ].map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`v2-coop-tab-${k}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
                  tab === k ? "bg-[#A78BFA] text-[#06061A] shadow-lg shadow-[#A78BFA]/30" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
          {tab === "deck"      && <DeckView />}
          {tab === "matches"   && <MatchesView />}
          {tab === "groups"    && <GroupsView />}
          {tab === "completed" && <CompletedView />}
          {tab === "profile"   && <Onboarding refresh={refresh} initial={me.profile} edit />}
        </>
      )}
    </div>
  );
}

/* ================================================================ */
function Onboarding({ refresh, initial, edit = false }) {
  const [emoji, setEmoji] = useState(initial?.avatar_emoji || "🎮");
  const [photoUrl, setPhotoUrl] = useState(initial?.photo_url || "");
  const [platform, setPlatform] = useState(initial?.play_platform || "");
  const [games, setGames] = useState(() => {
    const seed = initial?.games_raw || initial?.games_enriched?.map(g => g.original || g.corrected) || [];
    while (seed.length < 5) seed.push("");
    return seed.slice(0, 5);
  });
  const [wishlist, setWishlist] = useState(() => {
    const seed = initial?.wishlist_raw || initial?.wishlist_enriched?.map(g => g.original || g.corrected) || [];
    while (seed.length < 5) seed.push("");
    return seed.slice(0, 5);
  });
  const [busy, setBusy] = useState(false);

  const setGameAt = (i, v) => setGames(arr => arr.map((g, idx) => idx === i ? v : g));
  const setWishAt = (i, v) => setWishlist(arr => arr.map((g, idx) => idx === i ? v : g));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = games.map(g => g.trim()).filter(Boolean);
    const cleanedWl = wishlist.map(g => g.trim()).filter(Boolean);
    if (cleaned.length === 0) return toast.error("Add at least one game.");
    if (!platform) return toast.error("Pick the platform you play on.");
    setBusy(true);
    try {
      await api.post("/coop/profile", {
        avatar_emoji: emoji,
        photo_url: photoUrl.trim() || null,
        games: cleaned,
        play_platform: platform,
        coop_wishlist: cleanedWl,
      });
      toast.success(edit ? "Profile updated. Cards refreshed." : "Profile created! Let's find your squad.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="v2-coop-onboarding" className="space-y-4">
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#1a0a3e] via-[#1c0e4f] to-[#0a0420] ring-2 ring-[#A78BFA]/40 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#A78BFA]">
          {edit ? "// Edit profile" : "// Get started"}
        </div>

        {/* Emoji */}
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/70">Pick your avatar emoji</span>
          <input
            type="text" value={emoji} onChange={e => setEmoji(e.target.value)}
            maxLength={4}
            data-testid="v2-coop-emoji"
            style={{ fontSize: "32px" }}
            className="mt-2 w-20 h-20 rounded-2xl bg-white/5 ring-1 ring-white/10 text-center outline-none focus:ring-[#A78BFA]"
          />
        </label>

        {/* Photo URL */}
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/70">Photo URL <span className="text-white/40">(optional, falls back to emoji)</span></span>
          <input
            type="url" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)}
            placeholder="https://i.pravatar.cc/300?u=me"
            maxLength={500}
            data-testid="v2-coop-photo"
            style={{ fontSize: "16px" }}
            className="mt-2 w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:ring-[#A78BFA]"
          />
        </label>

        {/* Platform */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-2">
            Platform <span className="text-[#FFD700]">(+20% match bonus on same platform)</span>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="v2-coop-platform-picker">
            {PLATFORMS.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatform(p)}
                data-testid={`v2-coop-platform-${p}`}
                className={`py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all ${
                  platform === p
                    ? "bg-[#A78BFA] text-[#06061A] ring-2 ring-[#A78BFA] shadow-lg shadow-[#A78BFA]/30"
                    : "bg-white/5 text-white/70 ring-1 ring-white/10 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Games */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-2">
            Your top 5 games <span className="text-white/40">(typos are fine — AI fixes them)</span>
          </div>
          <div className="space-y-2" data-testid="v2-coop-games-list">
            {games.map((g, i) => (
              <input
                key={i}
                type="text" value={g}
                onChange={e => setGameAt(i, e.target.value)}
                placeholder={`Game ${i + 1}`}
                maxLength={80}
                data-testid={`v2-coop-game-${i}`}
                style={{ fontSize: "16px" }}
                className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:ring-[#A78BFA]"
              />
            ))}
          </div>
        </div>

        {/* Wishlist */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-2">
            Co-op games you want to play <span className="text-[#FFD700]">(+6 per shared title)</span>
          </div>
          <p className="text-[10px] text-white/40 mb-2">
            Up to 5 titles. Match boosts when another player already plays — or also wants to play — one of these.
          </p>
          <div className="space-y-2" data-testid="v2-coop-wishlist-list">
            {wishlist.map((g, i) => (
              <input
                key={i}
                type="text" value={g}
                onChange={e => setWishAt(i, e.target.value)}
                placeholder={`Wishlist ${i + 1}`}
                maxLength={80}
                data-testid={`v2-coop-wishlist-${i}`}
                style={{ fontSize: "16px" }}
                className="w-full bg-white/5 ring-1 ring-[#FFD700]/20 rounded-xl px-3 py-2.5 text-white outline-none focus:ring-[#FFD700]"
              />
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="v2-coop-save"
          className="w-full py-3 rounded-full bg-[#A78BFA] text-[#06061A] text-[12px] font-extrabold uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-[#A78BFA]/30"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (edit ? "Save changes" : "Find my squad")}
        </button>
        <p className="text-[10px] text-white/50 text-center">
          We'll analyse your games and rank other players by gaming compatibility.
        </p>
      </div>
    </form>
  );
}

/* ================================================================ */
function DeckView() {
  const [cards, setCards] = useState(null);
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState({ x: 0, active: false });
  const [expandedCard, setExpandedCard] = useState(null);
  const [animOut, setAnimOut] = useState(null); // {dir: 'like'|'dislike'}
  const startX = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coop/deck?limit=20");
      setCards(data.cards || []);
      setIdx(0);
    } catch (err) {
      setCards([]);
      toast.error(err?.response?.data?.detail || "Couldn't load deck.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const swipe = async (action, target) => {
    setAnimOut({ dir: action });
    try {
      const { data } = await api.post("/coop/swipe", { target_id: target.user_id, action });
      if (data?.matched) {
        toast.success("It's a match! 🎉");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Swipe failed.");
    }
    setTimeout(() => {
      setIdx(i => i + 1);
      setDrag({ x: 0, active: false });
      setAnimOut(null);
      setExpandedCard(null);
    }, 280);
  };

  if (cards === null) return <CenterLoader />;
  const visible = cards.slice(idx, idx + 5);
  const top = visible[0];

  // Pointer/touch handlers (rendered on the top card)
  const onDown = (e) => {
    if (animOut) return;
    startX.current = (e.touches ? e.touches[0].clientX : e.clientX);
    setDrag({ x: 0, active: true });
  };
  const onMove = (e) => {
    if (!drag.active) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    setDrag({ x: cx - startX.current, active: true });
  };
  const onUp = () => {
    if (!drag.active) return;
    if (drag.x > 120 && top) {
      swipe("like", top.profile);
    } else if (drag.x < -120 && top) {
      swipe("dislike", top.profile);
    } else {
      setDrag({ x: 0, active: false });
    }
  };

  if (!top) {
    return (
      <div className="rounded-3xl p-8 bg-white/5 ring-1 ring-white/10 text-center space-y-3" data-testid="v2-coop-empty">
        <Sparkles className="w-10 h-10 mx-auto text-[#A78BFA]" />
        <div className="text-base font-bold text-white">No more players to show.</div>
        <p className="text-[12px] text-white/60">
          Come back later — new players join all the time. You can also tweak your profile to broaden the match.
        </p>
        <button type="button" onClick={load} data-testid="v2-coop-refresh"
          className="px-4 py-2 rounded-full bg-[#A78BFA] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest">
          Refresh deck
        </button>
      </div>
    );
  }

  // Compute card transforms (top, behind1, behind2…)
  return (
    <div className="select-none" data-testid="v2-coop-deck">
      <div
        className="relative mx-auto"
        style={{ height: 540 }}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
      >
        {visible.map((card, i) => {
          const isTop = i === 0;
          const rot = isTop ? drag.x * 0.05 : 0;
          const dx  = isTop ? drag.x : 0;
          let outTx = "";
          if (isTop && animOut) {
            const sign = animOut.dir === "like" ? 1 : -1;
            outTx = `translateX(${sign * 600}px) rotate(${sign * 25}deg)`;
          }
          const scale  = 1 - i * 0.04;
          const yShift = i * 10;
          const z      = visible.length - i;
          return (
            <div
              key={`${card.profile.user_id}-${idx + i}`}
              className={`absolute inset-x-0 mx-auto ${isTop ? "cursor-grab active:cursor-grabbing" : "pointer-events-none"}`}
              onMouseDown={isTop ? onDown : undefined}
              onTouchStart={isTop ? onDown : undefined}
              onTouchMove={isTop ? onMove : undefined}
              onTouchEnd={isTop ? onUp : undefined}
              onClick={isTop && Math.abs(drag.x) < 6 && !animOut ? () => setExpandedCard(card) : undefined}
              style={{
                zIndex: z,
                transform: outTx || `translate(${dx}px, ${yShift}px) rotate(${rot}deg) scale(${scale})`,
                transition: drag.active && isTop ? "none" : "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
                maxWidth: 360,
              }}
              data-testid={isTop ? "v2-coop-top-card" : `v2-coop-card-stack-${i}`}
            >
              <PlayerCard card={card} dragX={isTop ? drag.x : 0} />
            </div>
          );
        })}
      </div>

      {/* Action buttons under the deck */}
      <div className="mt-4 flex items-center justify-center gap-6">
        <button
          type="button"
          onClick={() => top && swipe("dislike", top.profile)}
          data-testid="v2-coop-action-dislike"
          className="w-14 h-14 rounded-full bg-white/5 ring-2 ring-[#F87171]/60 flex items-center justify-center hover:bg-[#F87171]/10 hover:ring-[#F87171] transition-colors"
        >
          <X className="w-7 h-7 text-[#F87171]" />
        </button>
        <button
          type="button"
          onClick={() => top && setExpandedCard(top)}
          data-testid="v2-coop-action-view"
          className="w-12 h-12 rounded-full bg-white/5 ring-2 ring-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
        <button
          type="button"
          onClick={() => top && swipe("like", top.profile)}
          data-testid="v2-coop-action-like"
          className="w-14 h-14 rounded-full bg-[#00FF87]/10 ring-2 ring-[#00FF87] flex items-center justify-center hover:bg-[#00FF87]/20 transition-colors shadow-lg shadow-[#00FF87]/20"
        >
          <Heart className="w-7 h-7 text-[#00FF87]" />
        </button>
      </div>

      <div className="mt-3 text-center text-[10px] font-mono uppercase tracking-widest text-white/40">
        {cards.length - idx} player{cards.length - idx === 1 ? "" : "s"} left
      </div>

      {expandedCard && (
        <CardDetailModal
          card={expandedCard}
          onClose={() => setExpandedCard(null)}
          onLike={() => swipe("like", expandedCard.profile)}
          onDislike={() => swipe("dislike", expandedCard.profile)}
        />
      )}
    </div>
  );
}

/* ================================================================ */
function PlayerCard({ card, dragX }) {
  const { profile, score, reason, shared_games, wishlist_shared, same_platform } = card;
  const relPct = (profile.reliability_pct == null) ? null : Number(profile.reliability_pct);
  const decision = dragX > 60 ? "like" : dragX < -60 ? "dislike" : null;
  // Skin-style rim — hotter rim for higher MATCH score, matching FUT rarity vibe
  const rim =
    score >= 85 ? "from-[#FFD700] via-[#7C3AED] to-[#00FF87]" :
    score >= 70 ? "from-[#7C3AED] via-[#22D3EE] to-[#00FF87]" :
    score >= 50 ? "from-[#FFCC00] via-[#7C3AED] to-[#64748B]" :
                  "from-[#E11D48] via-[#7C3AED] to-[#64748B]";
  return (
    <div className="relative rounded-[10px] p-[2px]" style={{ minHeight: 540 }}>
      {/* Rim gradient */}
      <div className={`absolute inset-0 rounded-[10px] bg-gradient-to-b ${rim} opacity-95 pointer-events-none`} aria-hidden />
      {/* Inner card */}
      <div className="relative rounded-[8px] overflow-hidden bg-[#0A0A0A] border border-black">
        {/* FUT backdrop */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#0E0E14] via-[#1A1735] to-[#0A0A0A]" aria-hidden />
        <div className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.55),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(0,255,135,0.32),transparent_55%)]" aria-hidden />

        <div className="relative px-5 pt-4 pb-3">
          {/* TOP STRIP: MATCH score (top-left) + REL score (top-right) */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col items-start" data-testid="v2-coop-card-match">
              <span className="font-heading text-[44px] leading-none text-white drop-shadow-[0_3px_8px_rgba(0,0,0,0.7)] tabular-nums">
                {score}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#00FF87] mt-0.5">MATCH</span>
            </div>
            <div className="flex flex-col items-end" data-testid="v2-coop-card-rel">
              {relPct == null ? (
                <>
                  <span className="font-heading text-[44px] leading-none text-white/30 tabular-nums">—</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mt-0.5">UNRATED</span>
                </>
              ) : (
                <>
                  <span className="font-heading text-[44px] leading-none text-white drop-shadow-[0_3px_8px_rgba(0,0,0,0.7)] tabular-nums">
                    {relPct}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#22D3EE] mt-0.5">REL</span>
                </>
              )}
            </div>
          </div>

          {/* Avatar — uploaded photo or silhouette */}
          <div className="relative mx-auto my-3 w-28 h-28 flex items-end justify-center">
            {profile.photo_url ? (
              <img
                src={profile.photo_url}
                alt={profile.username}
                loading="lazy"
                decoding="async"
                data-testid="v2-coop-card-photo"
                className="relative w-28 h-28 rounded-full object-cover ring-2 ring-white/30 shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
              />
            ) : (
              <div
                data-testid="v2-coop-card-avatar"
                className="relative w-28 h-28 rounded-full bg-gradient-to-br from-[#0A0A0A] to-[#1A1735] ring-2 ring-white/10 flex items-center justify-center shadow-[0_6px_16px_rgba(0,0,0,0.6)] text-6xl"
              >
                {profile.avatar_emoji || "🎮"}
              </div>
            )}
            <span className="absolute -bottom-1 right-2 w-3 h-3 rounded-full bg-[#22C55E] ring-2 ring-[#0A0A0A]" />
          </div>

          {/* Username */}
          <div className="font-heading uppercase text-xl text-white text-center tracking-wider drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] line-clamp-1">
            @{profile.username}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/55 text-center mt-0.5 flex items-center justify-center gap-1.5">
            {profile.play_platform && (
              <span style={{ color: same_platform ? "#FFD700" : undefined }}>
                {same_platform && "✓ "}{profile.play_platform}
              </span>
            )}
            <span className="text-white/30">·</span>
            <span>{(profile.platform || "mobile").toUpperCase()}</span>
          </div>

          {/* Stats strip — FUT footer */}
          <div className="mt-3 grid grid-cols-3 gap-1 bg-black/55 border border-white/10 rounded-sm px-2 py-2 backdrop-blur-sm">
            <Stat label="SHARED" value={shared_games?.length || 0} accent="text-[#00FF87]" />
            <Stat label="WISH"   value={wishlist_shared?.length || 0} accent="text-[#FFD700]" />
            <Stat label="GAMES"  value={(profile.games_enriched || []).length} accent="text-white" />
          </div>

          {/* Reason text */}
          <p className="mt-3 text-[11.5px] text-white/85 leading-snug line-clamp-3 min-h-[42px]">{reason}</p>

          {/* Shared games + wishlist mini-strips */}
          {(shared_games?.length || 0) > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {shared_games.slice(0, 4).map((g, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-sm bg-[#00FF87]/20 text-[9px] font-bold text-[#00FF87] uppercase tracking-widest border border-[#00FF87]/30">
                  {g}
                </span>
              ))}
            </div>
          )}
          {(wishlist_shared?.length || 0) > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {wishlist_shared.slice(0, 4).map((g, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-sm bg-[#FFD700]/15 text-[9px] font-bold text-[#FFD700] uppercase tracking-widest border border-[#FFD700]/30">
                  ★ {g}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Swipe decision overlay */}
        {decision && (
          <div
            className="absolute top-6 left-6 text-3xl font-heading uppercase tracking-widest pointer-events-none"
            style={{
              color: decision === "like" ? "#00FF87" : "#F87171",
              transform: decision === "like" ? "rotate(-14deg)" : "rotate(14deg)",
              border: `4px solid ${decision === "like" ? "#00FF87" : "#F87171"}`,
              padding: "4px 12px",
              borderRadius: 8,
              opacity: Math.min(1, Math.abs(dragX) / 120),
            }}
          >
            {decision === "like" ? "LIKE" : "NOPE"}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-score text-lg leading-none tabular-nums ${accent}`}>{value}</span>
      <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/50 mt-0.5">{label}</span>
    </div>
  );
}

/* ================================================================ */
function CardDetailModal({ card, onClose, onLike, onDislike }) {
  const { profile, score, breakdown, reason, shared_games, wishlist_shared, same_platform } = card;
  const c = scoreColor(score);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="v2-coop-detail-modal"
    >
      <div
        className="max-w-md w-full rounded-3xl bg-gradient-to-br from-[#1a0a3e] via-[#1c0e4f] to-[#0a0420] ring-2 ring-[#A78BFA]/40 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6">
          <button
            type="button"
            onClick={onClose}
            data-testid="v2-coop-detail-close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center text-5xl">
              {profile.avatar_emoji || "🎮"}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-extrabold text-white truncate">@{profile.username}</h3>
              {profile.club_name && <div className="text-[10px] font-mono text-white/50">{profile.club_name}</div>}
              {profile.play_platform && (
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold"
                  style={{
                    background: same_platform ? "#FFD70022" : "rgba(255,255,255,0.05)",
                    color: same_platform ? "#FFD700" : "#A1A1AA",
                  }}>
                  {same_platform && "✓ "}{profile.play_platform}
                </div>
              )}
            </div>
            <div
              className="px-3 py-1.5 rounded-full text-sm font-extrabold ring-2"
              style={{ color: c.fg, background: c.bg + "20", borderColor: c.fg }}
            >
              {score}%
            </div>
          </div>

          <p className="mt-3 text-[13px] text-white/85">{reason}</p>

          {/* Breakdown pills */}
          {breakdown && Object.keys(breakdown).length > 0 && (
            <div className="mt-4">
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">Match breakdown</div>
              <div className="mt-2 grid grid-cols-3 gap-1.5">
                {[
                  ["exact",          "Same games"],
                  ["genre",          "Genre"],
                  ["subgenre",       "Subgenre"],
                  ["coop",           "Co-op"],
                  ["tone",           "Tone"],
                  ["era",            "Era"],
                  ["platform",       "Plat-hint"],
                  ["play_platform",  "Platform"],
                  ["wishlist",       "Wishlist"],
                ].map(([k, label]) => {
                  const v = breakdown[k] || 0;
                  if (!v) return null;
                  return (
                    <div key={k} className="px-2 py-1.5 rounded-lg bg-white/5 ring-1 ring-white/10 text-center" data-testid={`v2-coop-bd-${k}`}>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">{label}</div>
                      <div className="text-sm font-extrabold text-[#A78BFA]">+{v}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Shared games */}
          {shared_games?.length > 0 && (
            <div className="mt-4 rounded-xl bg-[#00FF87]/10 ring-1 ring-[#00FF87]/40 p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#00FF87]">Shared games</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {shared_games.map((g, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-[#00FF87] text-[10px] font-extrabold text-[#06120A]">{g}</span>
                ))}
              </div>
            </div>
          )}

          {/* Wishlist overlap */}
          {wishlist_shared?.length > 0 && (
            <div className="mt-3 rounded-xl bg-[#FFD700]/10 ring-1 ring-[#FFD700]/40 p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFD700]">★ Both want to play</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {wishlist_shared.map((g, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-[#FFD700] text-[10px] font-extrabold text-[#06061A]">{g}</span>
                ))}
              </div>
            </div>
          )}

          {/* Their wishlist */}
          {profile.wishlist_enriched?.length > 0 && (
            <div className="mt-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">Their co-op wishlist</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {profile.wishlist_enriched.map((g, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-[#FFD700]/20 text-[10px] font-bold text-white/80">
                    {g.corrected || g.original}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* All games */}
          <div className="mt-4">
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">Their games</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(profile.games_enriched || []).map((g, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10 text-[10px] font-bold text-white/80">
                  {g.corrected || g.original}
                </span>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button type="button" onClick={() => { onDislike(); onClose(); }}
              data-testid="v2-coop-detail-dislike"
              className="py-3 rounded-full bg-white/5 ring-1 ring-[#F87171]/40 text-[#F87171] text-[11px] font-extrabold uppercase tracking-widest hover:bg-[#F87171]/10">
              <X className="w-4 h-4 inline mr-1" /> Skip
            </button>
            <button type="button" onClick={() => { onLike(); onClose(); }}
              data-testid="v2-coop-detail-like"
              className="py-3 rounded-full bg-[#00FF87] text-[#06120A] text-[11px] font-extrabold uppercase tracking-widest shadow-lg shadow-[#00FF87]/30">
              <Heart className="w-4 h-4 inline mr-1" /> Like
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================ */
function MatchesView() {
  const [matches, setMatches] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/coop/matches");
        setMatches(data.matches || []);
      } catch { setMatches([]); }
    })();
  }, []);

  if (matches === null) return <CenterLoader />;
  if (matches.length === 0) return (
    <div className="rounded-3xl p-8 bg-white/5 ring-1 ring-white/10 text-center space-y-2" data-testid="v2-coop-matches-empty">
      <Trophy className="w-10 h-10 mx-auto text-[#A78BFA]" />
      <div className="text-base font-bold text-white">No matches yet</div>
      <p className="text-[12px] text-white/60">When you and another player both like each other, you'll see them here.</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="v2-coop-matches">
      {matches.map(m => (
        <Link
          key={m.match_id}
          to={`/v2/chat/${m.peer.user_id}`}
          data-testid={`v2-coop-match-${m.match_id}`}
          className="block rounded-2xl bg-white/5 ring-1 ring-white/10 hover:ring-[#A78BFA]/60 p-3 flex items-center gap-3 transition-shadow"
        >
          <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-3xl">
            {m.peer.avatar_emoji || "🎮"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">@{m.peer.username}</div>
            <div className="text-[10px] font-mono text-white/50">
              Matched {new Date(m.matched_at).toLocaleDateString()}
              {m.peer.play_platform && <> · {m.peer.play_platform}</>}
            </div>
            <ReliabilityStars score={m.peer.reliability_score} count={m.peer.reliability_count} className="mt-1" small />
          </div>
          <MessageSquare className="w-4 h-4 text-[#A78BFA]" />
        </Link>
      ))}
    </div>
  );
}

/* ================================================================ */
function ReliabilityStars({ score, count, className = "", small = false }) {
  if (score == null) {
    return (
      <div className={`flex items-center gap-1 ${className}`} data-testid="v2-coop-reliab-empty">
        <Star className={`${small ? "w-3 h-3" : "w-3.5 h-3.5"} text-white/20`} />
        <span className={`${small ? "text-[9px]" : "text-[10px]"} font-mono text-white/40`}>unrated</span>
      </div>
    );
  }
  const rounded = Math.round(score * 2) / 2;
  return (
    <div className={`flex items-center gap-0.5 ${className}`} data-testid="v2-coop-reliab">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = rounded >= i;
        const half = !filled && rounded + 0.5 >= i;
        return (
          <Star
            key={i}
            className={`${small ? "w-3 h-3" : "w-3.5 h-3.5"} ${
              filled ? "text-[#FFD700] fill-[#FFD700]" :
              half   ? "text-[#FFD700]" :
                       "text-white/15"
            }`}
            style={half ? { fill: "url(#half-star)" } : {}}
          />
        );
      })}
      <span className={`${small ? "text-[9px]" : "text-[10px]"} font-mono text-white/60 ml-1`}>
        {score.toFixed(1)}{count ? ` · ${count}` : ""}
      </span>
    </div>
  );
}

/* ================================================================ */
function GroupsView() {
  const [groups, setGroups] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [openMember, setOpenMember] = useState(null); // group obj being edited

  const refresh = useCallback(async () => {
    try {
      const [g, m] = await Promise.all([
        api.get("/coop/groups"),
        api.get("/coop/matches"),
      ]);
      setGroups(g.data.groups || []);
      setMatches(m.data.matches || []);
    } catch { setGroups([]); setMatches([]); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (groups === null) return <CenterLoader />;

  return (
    <div className="space-y-3" data-testid="v2-coop-groups">
      <button
        type="button"
        onClick={() => setShowCreate(true)}
        data-testid="v2-coop-create-group-btn"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-[#A78BFA] to-[#8B5CF6] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest shadow-lg shadow-[#A78BFA]/30"
      >
        <Plus className="w-4 h-4" /> Create new group
      </button>

      {groups.length === 0 ? (
        <div className="rounded-3xl p-8 bg-white/5 ring-1 ring-white/10 text-center space-y-2">
          <Users className="w-10 h-10 mx-auto text-[#A78BFA]" />
          <div className="text-base font-bold text-white">No groups yet</div>
          <p className="text-[12px] text-white/60">
            Create a co-op group and invite players you've already matched with.
          </p>
        </div>
      ) : (
        groups.map(g => (
          <GroupCard key={g.id} group={g} matches={matches} refresh={refresh}
            onAddMember={() => setOpenMember(g)} />
        ))
      )}

      {showCreate && (
        <CreateGroupModal
          matches={matches}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {openMember && (
        <AddMemberModal
          group={openMember}
          matches={matches}
          onClose={() => setOpenMember(null)}
          onSaved={() => { setOpenMember(null); refresh(); }}
        />
      )}
    </div>
  );
}

function GroupCard({ group, refresh, onAddMember }) {
  const { user } = useAuth();
  const isOwner = group.owner.user_id === user?.id;
  const [chatOpen, setChatOpen] = useState(false);

  const removeMember = async (uid) => {
    try {
      await api.delete(`/coop/groups/${group.id}/members/${uid}`);
      toast.success("Removed");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };
  const deleteGroup = async () => {
    if (!window.confirm(`Delete '${group.name}'? This cannot be undone.`)) return;
    try {
      await api.delete(`/coop/groups/${group.id}`);
      toast.success("Group deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };
  const leaveGroup = async () => {
    if (!window.confirm(`Leave '${group.name}'?`)) return;
    try {
      await api.delete(`/coop/groups/${group.id}/members/${user.id}`);
      toast.success("Left the group");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't leave");
    }
  };

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3" data-testid={`v2-coop-group-${group.id}`}>
      <div className="flex items-center gap-2">
        <div className="text-lg">👥</div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-extrabold text-white truncate">{group.name}</div>
          <div className="text-[10px] font-mono text-white/50">
            {group.member_count} member{group.member_count === 1 ? "" : "s"} · owner @{group.owner.username}
          </div>
        </div>
        {isOwner ? (
          <button type="button" onClick={deleteGroup} title="Delete group"
            data-testid={`v2-coop-group-delete-${group.id}`}
            className="p-1.5 rounded-full bg-[#E11D48]/20 text-[#F87171] hover:bg-[#E11D48]/30">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button type="button" onClick={leaveGroup} title="Leave group"
            data-testid={`v2-coop-group-leave-${group.id}`}
            className="text-[10px] font-bold uppercase tracking-widest text-white/60 hover:text-[#F87171]">
            Leave
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <MemberPill member={group.owner} isOwner />
        {group.members.map(m => (
          <MemberPill key={m.user_id} member={m} onRemove={isOwner ? () => removeMember(m.user_id) : null} />
        ))}
      </div>

      {isOwner && (
        <button type="button" onClick={onAddMember}
          data-testid={`v2-coop-group-add-member-${group.id}`}
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-white/5 ring-1 ring-white/10 hover:bg-white/10 text-[10px] font-bold uppercase tracking-widest text-white">
          <UserPlus className="w-3.5 h-3.5" /> Add member
        </button>
      )}

      <button type="button" onClick={() => setChatOpen(o => !o)}
        data-testid={`v2-coop-group-chat-toggle-${group.id}`}
        className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[#A78BFA]/15 ring-1 ring-[#A78BFA]/40 hover:bg-[#A78BFA]/25 text-[10px] font-bold uppercase tracking-widest text-[#A78BFA]"
      >
        <MessageSquare className="w-3.5 h-3.5" /> {chatOpen ? "Hide chat" : "Open group chat"}
      </button>

      {chatOpen && <GroupChatPanel groupId={group.id} />}
    </div>
  );
}

function GroupChatPanel({ groupId }) {
  const [msgs, setMsgs] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/coop/groups/${groupId}/chat`);
      setMsgs(data.messages || []);
    } catch (err) {
      setMsgs([]);
      toast.error(err?.response?.data?.detail || "Couldn't load chat");
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);
  useEffect(() => {
    if (msgs && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs?.length]);

  const send = async (e) => {
    e.preventDefault();
    const c = content.trim();
    if (!c) return;
    setBusy(true);
    try {
      await api.post(`/coop/groups/${groupId}/chat`, { content: c });
      setContent("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (msgs === null) return <CenterLoader />;
  return (
    <div className="rounded-2xl bg-black/30 ring-1 ring-[#A78BFA]/20 overflow-hidden flex flex-col" data-testid={`v2-coop-group-chat-${groupId}`}>
      <div className="max-h-72 overflow-y-auto p-2.5 space-y-1.5">
        {msgs.length === 0 ? (
          <div className="text-[11px] text-white/40 text-center py-6">No messages yet — say hi.</div>
        ) : msgs.map(m => (
          <div key={m.id} className="rounded-xl bg-white/5 px-2.5 py-1.5" data-testid={`v2-coop-group-msg-${m.id}`}>
            <div className="flex items-center justify-between text-[9px] font-mono text-white/40">
              <span>{m.avatar_emoji} @{m.username}</span>
              <span>{new Date(m.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <div className="text-[13px] text-white whitespace-pre-wrap break-words mt-0.5">{m.content}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-white/10 p-1.5 flex gap-1.5">
        <input type="text" value={content} onChange={e => setContent(e.target.value)}
          placeholder="Type a message…" maxLength={1000}
          data-testid={`v2-coop-group-chat-input-${groupId}`}
          style={{ fontSize: "16px" }}
          className="flex-1 bg-white/5 ring-1 ring-white/10 rounded-full px-3 py-1.5 text-white text-sm outline-none focus:ring-[#A78BFA]" />
        <button type="submit" disabled={busy || !content.trim()}
          data-testid={`v2-coop-group-chat-send-${groupId}`}
          className="px-3 py-1.5 rounded-full bg-[#A78BFA] text-[#06061A] disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}

function MemberPill({ member, isOwner, onRemove }) {
  return (
    <div className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10"
      data-testid={`v2-coop-member-${member.user_id}`}>
      <span className="text-base">{member.avatar_emoji || "🎮"}</span>
      <span className="text-[10px] font-bold text-white">@{member.username}</span>
      {isOwner && <span className="text-[8px] font-mono uppercase tracking-widest text-[#FFD700]">owner</span>}
      {onRemove && (
        <button type="button" onClick={onRemove} className="ml-0.5 text-[#F87171] hover:text-[#E11D48]"
          data-testid={`v2-coop-member-remove-${member.user_id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function CreateGroupModal({ matches, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (uid) => {
    setPicked(prev => {
      const n = new Set(prev);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Group name too short.");
    setBusy(true);
    try {
      await api.post("/coop/groups", { name: name.trim(), member_ids: Array.from(picked) });
      toast.success("Group created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="v2-coop-create-group-modal" title="Create group">
      <form onSubmit={submit} className="space-y-3">
        <input
          type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="Group name" maxLength={40}
          data-testid="v2-coop-create-group-name"
          style={{ fontSize: "16px" }}
          className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:ring-[#A78BFA]"
        />
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
          Invite from matches{matches.length ? ` (${matches.length})` : " — none yet"}
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {matches.length === 0 && (
            <div className="text-[11px] text-white/50">Match with someone first, then invite them.</div>
          )}
          {matches.map(m => {
            const sel = picked.has(m.peer.user_id);
            return (
              <button
                key={m.match_id} type="button" onClick={() => toggle(m.peer.user_id)}
                data-testid={`v2-coop-create-group-pick-${m.peer.user_id}`}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-xl transition-colors ${
                  sel ? "bg-[#A78BFA]/20 ring-2 ring-[#A78BFA]" : "bg-white/5 ring-1 ring-white/10 hover:bg-white/10"
                }`}
              >
                <div className="text-2xl">{m.peer.avatar_emoji || "🎮"}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">@{m.peer.username}</div>
                  {m.peer.play_platform && <div className="text-[10px] font-mono text-white/50">{m.peer.play_platform}</div>}
                </div>
                {sel && <Check className="w-4 h-4 text-[#A78BFA]" />}
              </button>
            );
          })}
        </div>
        <button type="submit" disabled={busy || name.trim().length < 2}
          data-testid="v2-coop-create-group-submit"
          className="w-full py-2.5 rounded-full bg-[#A78BFA] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Create${picked.size ? ` (${picked.size + 1} members)` : ""}`}
        </button>
      </form>
    </Modal>
  );
}

function AddMemberModal({ group, matches, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const ownerOrMember = new Set([group.owner.user_id, ...group.members.map(m => m.user_id)]);
  const available = matches.filter(m => !ownerOrMember.has(m.peer.user_id));

  const addOne = async (uid) => {
    setBusy(true);
    try {
      await api.post(`/coop/groups/${group.id}/members`, { user_id: uid });
      toast.success("Added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add");
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="v2-coop-add-member-modal" title={`Add to '${group.name}'`}>
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {available.length === 0 && (
          <div className="text-[12px] text-white/60 py-4 text-center">
            No more eligible matches. Match with more players first to invite them.
          </div>
        )}
        {available.map(m => (
          <button key={m.match_id} type="button" disabled={busy}
            onClick={() => addOne(m.peer.user_id)}
            data-testid={`v2-coop-add-member-${m.peer.user_id}`}
            className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
          >
            <div className="text-2xl">{m.peer.avatar_emoji || "🎮"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">@{m.peer.username}</div>
              {m.peer.play_platform && <div className="text-[10px] font-mono text-white/50">{m.peer.play_platform}</div>}
            </div>
            <UserPlus className="w-4 h-4 text-[#A78BFA]" />
          </button>
        ))}
      </div>
    </Modal>
  );
}

/* ================================================================ */
function CompletedView() {
  const [list, setList] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [rateRow, setRateRow] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        api.get("/coop/completed"),
        api.get("/coop/matches"),
      ]);
      setList(c.data.completed || []);
      setMatches(m.data.matches || []);
    } catch { setList([]); setMatches([]); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (list === null) return <CenterLoader />;

  return (
    <div className="space-y-3" data-testid="v2-coop-completed">
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        data-testid="v2-coop-completed-add-btn"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-[#A78BFA] to-[#8B5CF6] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest shadow-lg shadow-[#A78BFA]/30"
      >
        <Plus className="w-4 h-4" /> Add completed game
      </button>

      {list.length === 0 ? (
        <div className="rounded-3xl p-8 bg-white/5 ring-1 ring-white/10 text-center space-y-2">
          <ClipboardCheck className="w-10 h-10 mx-auto text-[#A78BFA]" />
          <div className="text-base font-bold text-white">No completed games yet</div>
          <p className="text-[12px] text-white/60">
            Finished a game with one of your matches? Log it here — once both of you confirm, you can rate each other.
          </p>
        </div>
      ) : (
        list.map(c => (
          <CompletedCard key={c.id} comp={c} refresh={refresh} onRate={() => setRateRow(c)} />
        ))
      )}

      {showAdd && (
        <AddCompletedModal
          matches={matches}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
      {rateRow && (
        <RateModal
          comp={rateRow}
          onClose={() => setRateRow(null)}
          onSaved={() => { setRateRow(null); refresh(); }}
        />
      )}
    </div>
  );
}

function CompletedCard({ comp, refresh, onRate }) {
  const { peer, game_title, status, i_proposed, my_rating, peer_rated, received_rating } = comp;
  const isPendingForMe = status === "pending" && !i_proposed;
  const isPendingMine  = status === "pending" && i_proposed;
  const canRate        = status === "confirmed" && !my_rating;

  const respond = async (accept) => {
    try {
      const url = accept ? `/coop/completed/${comp.id}/confirm` : `/coop/completed/${comp.id}/decline`;
      await api.post(url);
      toast.success(accept ? "Confirmed — you can now rate each other." : "Declined");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 space-y-3" data-testid={`v2-coop-completed-card-${comp.id}`}>
      <div className="flex items-center gap-3">
        <div className="text-3xl">{peer.avatar_emoji || "🎮"}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white truncate">{game_title}</div>
          <div className="text-[10px] font-mono text-white/50">
            with @{peer.username}{peer.play_platform && ` · ${peer.play_platform}`}
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {isPendingForMe && (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => respond(true)}
            data-testid={`v2-coop-completed-confirm-${comp.id}`}
            className="py-2 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest">
            <Check className="w-3.5 h-3.5 inline mr-1" /> Confirm
          </button>
          <button type="button" onClick={() => respond(false)}
            data-testid={`v2-coop-completed-decline-${comp.id}`}
            className="py-2 rounded-full bg-white/5 ring-1 ring-[#F87171]/40 text-[#F87171] text-[10px] font-extrabold uppercase tracking-widest">
            <X className="w-3.5 h-3.5 inline mr-1" /> Decline
          </button>
        </div>
      )}

      {isPendingMine && (
        <div className="text-[11px] text-white/60 text-center bg-white/5 rounded-xl py-2">
          Waiting for @{peer.username} to confirm…
        </div>
      )}

      {canRate && (
        <button type="button" onClick={onRate}
          data-testid={`v2-coop-completed-rate-${comp.id}`}
          className="w-full py-2.5 rounded-full bg-[#FFD700] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest shadow-lg shadow-[#FFD700]/30">
          <Star className="w-3.5 h-3.5 inline mr-1" /> Rate @{peer.username}
        </button>
      )}

      {my_rating && (
        <div className="rounded-xl bg-white/5 p-2.5">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-1">You rated them</div>
          <RatingSummary rating={my_rating} />
        </div>
      )}

      {received_rating && (
        <div className="rounded-xl bg-[#00FF87]/10 ring-1 ring-[#00FF87]/30 p-2.5">
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#00FF87] mb-1">@{peer.username} rated you</div>
          <RatingSummary rating={received_rating} />
        </div>
      )}

      {status === "confirmed" && !peer_rated && my_rating && (
        <div className="text-[10px] font-mono text-white/40 text-center">
          Waiting for @{peer.username} to rate you…
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:    { c: "bg-[#FFD700]/15 text-[#FFD700]", label: "pending" },
    confirmed:  { c: "bg-[#00FF87]/15 text-[#00FF87]", label: "confirmed" },
    declined:   { c: "bg-[#E11D48]/15 text-[#F87171]", label: "declined" },
  };
  const m = map[status] || { c: "bg-white/10 text-white/60", label: status };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest ${m.c}`}>
      {m.label}
    </span>
  );
}

function RatingSummary({ rating }) {
  const items = [
    ["communication", "Comm"],
    ["teamwork",      "Team"],
    ["skill",         "Skill"],
    ["punctuality",   "Punct"],
    ["vibe",          "Vibe"],
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(([k, label]) => (
        <div key={k} className="px-2 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10 text-[10px] font-bold text-white/90 inline-flex items-center gap-1">
          {label} <Star className="w-2.5 h-2.5 text-[#FFD700] fill-[#FFD700]" /> {rating[k]}
        </div>
      ))}
    </div>
  );
}

function AddCompletedModal({ matches, onClose, onSaved }) {
  const [partnerId, setPartnerId] = useState("");
  const [gameTitle, setGameTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!partnerId) return toast.error("Pick a partner.");
    if (gameTitle.trim().length < 1) return toast.error("Type a game title.");
    setBusy(true);
    try {
      await api.post("/coop/completed", { partner_id: partnerId, game_title: gameTitle.trim() });
      toast.success("Sent — waiting for your partner to confirm.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="v2-coop-add-completed-modal" title="Add completed game">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1.5">Game title</div>
          <input
            type="text" value={gameTitle} onChange={e => setGameTitle(e.target.value)}
            placeholder="e.g. Halo Infinite Campaign" maxLength={80}
            data-testid="v2-coop-add-completed-game"
            style={{ fontSize: "16px" }}
            className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:ring-[#A78BFA]"
          />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1.5">Played with</div>
          {matches.length === 0 ? (
            <div className="text-[11px] text-white/50 py-2">Match with someone first.</div>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {matches.map(m => (
                <button
                  key={m.match_id} type="button" onClick={() => setPartnerId(m.peer.user_id)}
                  data-testid={`v2-coop-add-completed-pick-${m.peer.user_id}`}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-xl transition-colors ${
                    partnerId === m.peer.user_id
                      ? "bg-[#A78BFA]/20 ring-2 ring-[#A78BFA]"
                      : "bg-white/5 ring-1 ring-white/10 hover:bg-white/10"
                  }`}
                >
                  <div className="text-2xl">{m.peer.avatar_emoji || "🎮"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white truncate">@{m.peer.username}</div>
                    {m.peer.play_platform && <div className="text-[10px] font-mono text-white/50">{m.peer.play_platform}</div>}
                  </div>
                  {partnerId === m.peer.user_id && <Check className="w-4 h-4 text-[#A78BFA]" />}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="submit" disabled={busy || !partnerId || gameTitle.trim().length < 1}
          data-testid="v2-coop-add-completed-submit"
          className="w-full py-2.5 rounded-full bg-[#A78BFA] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send for confirmation"}
        </button>
      </form>
    </Modal>
  );
}

function RateModal({ comp, onClose, onSaved }) {
  const [vals, setVals] = useState({ communication: 0, teamwork: 0, skill: 0, punctuality: 0, vibe: 0 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals(prev => ({ ...prev, [k]: v }));
  const items = [
    ["communication", "Communication", "Voice/chat clarity, callouts"],
    ["teamwork",      "Teamwork",      "Synergy, support plays"],
    ["skill",         "Skill",         "Gameplay competence"],
    ["punctuality",   "Punctuality",   "On time, didn't bail"],
    ["vibe",          "Vibe",          "Fun factor"],
  ];
  const allRated = items.every(([k]) => vals[k] > 0);
  const submit = async () => {
    if (!allRated) return toast.error("Rate every criterion (1–5 stars).");
    setBusy(true);
    try {
      await api.post(`/coop/completed/${comp.id}/rate`, vals);
      toast.success("Rating submitted");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="v2-coop-rate-modal" title={`Rate @${comp.peer.username}`}>
      <p className="text-[11px] text-white/60 mb-2">For <span className="text-white font-bold">{comp.game_title}</span></p>
      <div className="space-y-2.5">
        {items.map(([k, label, hint]) => (
          <div key={k} className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-white">{label}</div>
                <div className="text-[10px] font-mono text-white/40">{hint}</div>
              </div>
              <div className="text-[10px] font-mono text-[#FFD700] tabular-nums">{vals[k] || "—"}/5</div>
            </div>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n} type="button" onClick={() => set(k, n)}
                  data-testid={`v2-coop-rate-${k}-${n}`}
                  className="flex-1 p-1 rounded-md hover:bg-white/10"
                >
                  <Star className={`w-6 h-6 mx-auto ${vals[k] >= n ? "text-[#FFD700] fill-[#FFD700]" : "text-white/15"}`} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={submit} disabled={busy || !allRated}
        data-testid="v2-coop-rate-submit"
        className="w-full mt-4 py-3 rounded-full bg-[#FFD700] text-[#06061A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit rating"}
      </button>
    </Modal>
  );
}

/* ================================================================ */
function Modal({ children, onClose, testid, title }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="max-w-md w-full rounded-3xl bg-gradient-to-br from-[#1a0a3e] via-[#1c0e4f] to-[#0a0420] ring-2 ring-[#A78BFA]/40 overflow-hidden shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold text-white">{title}</h3>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ================================================================ */
function scoreColor(score) {
  if (score >= 80) return { fg: "#00FF87", bg: "#00FF87" };  // green
  if (score >= 55) return { fg: "#FFD700", bg: "#FFD700" };  // yellow
  if (score >= 30) return { fg: "#F97316", bg: "#F97316" };  // orange
  return { fg: "#F87171", bg: "#F87171" };                   // red
}

function CenterLoader() {
  return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#A78BFA]" /></div>;
}
